import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import { applyForAJob } from '../services/candidateJobsService';
import { 
  showTransparentPreloaderAction, 
  hideTransparentPreloaderAction,
  showNotificationAction 
} from '../store/application/slice';
import { getCandidateCalendarData } from '../store/candidate/thunks';
import { toast } from 'react-toastify';
import { isFulfilled } from '@reduxjs/toolkit';
import { TJobData } from '../store/candidate/types';
import { NOTIFICATION_TIME_OUT } from '../constants/app';
import { VACANCY_SELECTION_METHOD, VACANCY_CANDIDATE_VISIBILITY } from '../models/vacancy';
import { getCandidateLabel } from '../components/Candidate/Jobs/CandidateVacancyCard';

export interface JobApplicationResult {
  success: boolean;
  actionType: 'applied' | 'accepted';
  restrictionReached?: boolean;
  timeframeConflict?: boolean;
}

export interface JobApplicationCallbacks {
  setShowAcceptedJobs?: (show: boolean) => void;
  setShowViewModal?: (show: boolean) => void;
  getCandidateJobList?: () => void;
  handleView?: (id: number) => void;
}

export const useJobApplication = () => {
  const dispatch = useAppDispatch();

  const maxDistanceAway = useAppSelector((state) => state.candidateJobs.maxDistanceAwayForMyCalendar)
  const profileData = useAppSelector(state => state.onboarding.userpersonaldetails);

  const applyForJob = useCallback(async (vacancy: TJobData): Promise<JobApplicationResult> => {
    dispatch(showTransparentPreloaderAction());
    
    try {
      const candidateUuid = profileData?.uuid;

      if (!candidateUuid) {
        toast.error("Please complete your profile before applying for a job.");
        return { success: false, actionType: 'applied' };
      }

      const actionType = getCandidateLabel({
        selectionMethod: vacancy.selection_method as VACANCY_SELECTION_METHOD,
        visibilityType: vacancy.visibility_type as VACANCY_CANDIDATE_VISIBILITY,
        visibilityUpgraded: vacancy.visibility_upgraded,
      }) === "Accept" ? "accepted" : "applied";

      const requestData = {
        action: actionType,
        uuid: vacancy?.uuid,
        candidate_uuid: candidateUuid,
        selection_method: vacancy?.selection_method,
        isResponseTimeGotOver: Boolean(vacancy?.visibility_upgraded),
      };

      const response = await applyForAJob(dispatch, requestData);
      const payload = (response?.payload ?? {}) as any;
      const restrictionReached = payload?.data?.apply_restricted === true;
      const timeframeConflict = payload?.data?.is_timeframe_conflict === true;

      return {
        success: isFulfilled(response),
        actionType,
        restrictionReached,
        timeframeConflict
      };

    } catch (error) {
      console.error("Failed to apply for the job:", error);
      return { success: false, actionType: 'applied' };
    } finally {
      dispatch(hideTransparentPreloaderAction());
    }
  }, [dispatch, profileData?.uuid]);

  const handleJobAccepted = useCallback(async (
    vacancyDetails: any,
    callbacks: JobApplicationCallbacks
  ) => {
    callbacks.setShowAcceptedJobs?.(false);
    callbacks.setShowViewModal?.(false);
    
    // dispatch(showNotificationAction("Job Accepted"));
    
    // setTimeout(async () => {
      dispatch(showTransparentPreloaderAction());
      await dispatch(getCandidateCalendarData({ distance_away: maxDistanceAway}));
      if (callbacks.getCandidateJobList) {
        const result = callbacks.getCandidateJobList();
        if ((result as any) instanceof Promise) {
          await result;
        }
      } 
      
      callbacks.setShowViewModal?.(true)
      setTimeout( async() => {
        if (callbacks.handleView) {
          const result = callbacks.handleView(vacancyDetails?.centre_id);
          if ((result as any) instanceof Promise) {
            await result;
          }
        }
      }, 300);

      dispatch(hideTransparentPreloaderAction());
    // }, NOTIFICATION_TIME_OUT);
  }, [dispatch, maxDistanceAway]);

  return { applyForJob, handleJobAccepted };
};